import { useEffect, useMemo, useState } from "react";
import Modal from "../../../UI/Modal/Modal";
import InputField from "../../../UI/Form/InputField";
import Button from "../../../UI/Button/Button";
import ButtonLoading from "../../../UI/Button/ButtonLoading";
import {
  CreateExercisePayload,
  CreateQuestionPayload,
  EducationalLevel,
  EducationalSystem,
  QuestionType,
} from "../../../Types/Courses";

interface CreateExerciseModalProps {
  open: boolean;
  loading: boolean;
  systems: EducationalSystem[];
  levels: EducationalLevel[];
  defaultSystemId?: number | string;
  defaultLevelId?: number | string;
  onClose: () => void;
  onSubmit: (payload: CreateExercisePayload, questions: CreateQuestionPayload[]) => Promise<void>;
}

interface DraftQuestionFormState {
  localId?: string;
  type: QuestionType;
  question_text: string;
  points: number;
  choices: string[];
  correct_choice_index: number;
  correct_value: string;
}

const questionTypes: QuestionType[] = [
  "multiple_choice",
  "single_choice",
  "short_answer",
  "number",
];

const defaultDraftQuestion: DraftQuestionFormState = {
  type: "single_choice",
  question_text: "",
  points: 1,
  choices: ["", ""],
  correct_choice_index: 0,
  correct_value: "",
};

type WizardStep = 1 | 2 | 3;

export default function CreateExerciseModal(props: CreateExerciseModalProps) {
  const {
    open,
    loading,
    systems,
    levels,
    defaultSystemId = "",
    defaultLevelId = "",
    onClose,
    onSubmit,
  } = props;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [educationalSystemId, setEducationalSystemId] = useState<number | string>(defaultSystemId);
  const [levelId, setLevelId] = useState<number | string>(defaultLevelId);
  const [isGlobal, setIsGlobal] = useState(true);
  const [questionForm, setQuestionForm] = useState<DraftQuestionFormState>(defaultDraftQuestion);
  const [draftQuestions, setDraftQuestions] = useState<DraftQuestionFormState[]>([]);
  const [step, setStep] = useState<WizardStep>(1);
  const [error, setError] = useState("");
  const [questionError, setQuestionError] = useState("");

  const filteredLevels = useMemo(() => {
    if (!educationalSystemId) {
      return levels;
    }
    return levels.filter((item) => item.educational_system_id === Number(educationalSystemId));
  }, [levels, educationalSystemId]);

  useEffect(() => {
    if (!open) return;
    setEducationalSystemId(defaultSystemId);
    setLevelId(defaultLevelId);
  }, [defaultLevelId, defaultSystemId, open]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setEducationalSystemId(defaultSystemId);
    setLevelId(defaultLevelId);
    setIsGlobal(true);
    setQuestionForm(defaultDraftQuestion);
    setDraftQuestions([]);
    setStep(1);
    setError("");
    setQuestionError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const buildQuestionPayload = (question: DraftQuestionFormState): CreateQuestionPayload => {
    let options: Record<string, unknown> | undefined;
    let correct_answer: Record<string, unknown> | undefined;

    if (question.type === "single_choice" || question.type === "multiple_choice") {
      const choices = question.choices.map((item) => item.trim()).filter(Boolean);
      if (choices.length < 2) {
        throw new Error("Ajoute au moins 2 choix pour les questions à choix.");
      }
      if (question.correct_choice_index >= choices.length) {
        throw new Error("Sélectionne une réponse correcte valide.");
      }
      options = { choices };
      correct_answer = { index: question.correct_choice_index };
    } else if (question.type === "number") {
      if (!String(question.correct_value).trim()) {
        throw new Error("La réponse numérique correcte est obligatoire.");
      }
      correct_answer = { value: Number(question.correct_value) };
    } else {
      if (!question.correct_value.trim()) {
        throw new Error("La réponse correcte est obligatoire.");
      }
      correct_answer = { value: question.correct_value.trim() };
    }

    if (!question.question_text.trim()) {
      throw new Error("Le texte de la question est obligatoire.");
    }

    return {
      type: question.type,
      question_text: question.question_text.trim(),
      options,
      correct_answer,
      points: Number(question.points) || 1,
    };
  };

  const addOrUpdateQuestionDraft = () => {
    try {
      buildQuestionPayload(questionForm);
      setQuestionError("");
    } catch (buildError: any) {
      setQuestionError(buildError?.message || "Question invalide");
      return;
    }

    if (questionForm.localId) {
      setDraftQuestions((previous) =>
        previous.map((item) => (item.localId === questionForm.localId ? { ...questionForm } : item))
      );
    } else {
      setDraftQuestions((previous) => [
        ...previous,
        { ...questionForm, localId: `draft-${Date.now()}-${previous.length}` },
      ]);
    }
    setQuestionForm(defaultDraftQuestion);
  };

  const removeQuestionDraft = (localId?: string) => {
    if (!localId) return;
    setDraftQuestions((previous) => previous.filter((item) => item.localId !== localId));
    if (questionForm.localId === localId) {
      setQuestionForm(defaultDraftQuestion);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Le titre est obligatoire");
      return;
    }
    if (!draftQuestions.length) {
      setError("Ajoute au moins une question avant de créer et associer l'exercice.");
      return;
    }

    setError("");
    try {
      const questionPayloads = draftQuestions.map((question) => buildQuestionPayload(question));
      await onSubmit(
        {
          title: title.trim(),
          description: description.trim() || null,
          educational_system_id: educationalSystemId ? Number(educationalSystemId) : null,
          level_id: levelId ? Number(levelId) : null,
          is_global: isGlobal,
        },
        questionPayloads
      );
      resetForm();
    } catch (submitError: any) {
      setError(submitError?.message || "Impossible de créer cet exercice.");
    }
  };

  const validateStepOne = () => {
    if (!title.trim()) {
      setError("Le titre est obligatoire");
      return false;
    }
    setError("");
    return true;
  };

  const validateStepTwo = () => {
    if (!draftQuestions.length) {
      setError("Ajoute au moins une question avant de continuer.");
      return false;
    }
    setError("");
    return true;
  };

  const goToNextStep = () => {
    if (step === 1 && !validateStepOne()) return;
    if (step === 2 && !validateStepTwo()) return;
    setStep((previous) => {
      if (previous === 1) return 2;
      if (previous === 2) return 3;
      return 3;
    });
  };

  const goToPreviousStep = () => {
    setStep((previous) => {
      if (previous === 3) return 2;
      if (previous === 2) return 1;
      return 1;
    });
    setError("");
  };

  return (
    <Modal open={open} onClose={handleClose} title="Créer un exercice sans quitter l'éditeur">
      <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div
            className={`rounded p-2 text-center border ${
              step >= 1 ? "bg-primary/15 border-primary font-semibold" : "border-gray-200 text-gray-500"
            }`}
          >
            1. Infos
          </div>
          <div
            className={`rounded p-2 text-center border ${
              step >= 2 ? "bg-primary/15 border-primary font-semibold" : "border-gray-200 text-gray-500"
            }`}
          >
            2. Questions
          </div>
          <div
            className={`rounded p-2 text-center border ${
              step >= 3 ? "bg-primary/15 border-primary font-semibold" : "border-gray-200 text-gray-500"
            }`}
          >
            3. Validation
          </div>
        </div>

        {step === 1 ? (
          <div className="border rounded-lg p-3 space-y-3">
            <h4 className="font-medium">Informations de l'exercice</h4>
            <div>
              <label className="text-sm font-medium">Titre</label>
              <InputField value={title} onChange={(event) => setTitle(event.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="w-full form-input"
                rows={4}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Système éducatif</label>
                <select
                  className="w-full form-select"
                  value={educationalSystemId}
                  onChange={(event) => {
                    const value = event.target.value ? Number(event.target.value) : "";
                    setEducationalSystemId(value);
                    setLevelId("");
                  }}
                >
                  <option value="">-- Sélectionner --</option>
                  {systems.map((system) => (
                    <option key={system.id} value={system.id}>
                      {system.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Niveau</label>
                <select
                  className="w-full form-select"
                  value={levelId}
                  onChange={(event) =>
                    setLevelId(event.target.value ? Number(event.target.value) : "")
                  }
                >
                  <option value="">-- Sélectionner --</option>
                  {filteredLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={(event) => setIsGlobal(event.target.checked)}
                />
                Exercice global
              </label>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Questions de l'exercice</h4>
              <Button onClick={() => setQuestionForm(defaultDraftQuestion)}>Nouvelle question</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Type</label>
                <select
                  className="w-full form-select"
                  value={questionForm.type}
                  onChange={(event) =>
                    setQuestionForm((previous) => ({
                      ...previous,
                      type: event.target.value as QuestionType,
                    }))
                  }
                >
                  {questionTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Points</label>
                <InputField
                  type="number"
                  min={1}
                  value={questionForm.points}
                  onChange={(event) =>
                    setQuestionForm((previous) => ({
                      ...previous,
                      points: Number(event.target.value) || 1,
                    }))
                  }
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Texte de la question</label>
                <textarea
                  className="w-full form-input"
                  rows={2}
                  value={questionForm.question_text}
                  onChange={(event) =>
                    setQuestionForm((previous) => ({
                      ...previous,
                      question_text: event.target.value,
                    }))
                  }
                />
              </div>

              {(questionForm.type === "single_choice" || questionForm.type === "multiple_choice") && (
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-medium">Choix</label>
                  {questionForm.choices.map((choice, index) => (
                    <div key={`${index}`} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="draft_correct_choice"
                        checked={questionForm.correct_choice_index === index}
                        onChange={() =>
                          setQuestionForm((previous) => ({ ...previous, correct_choice_index: index }))
                        }
                      />
                      <InputField
                        value={choice}
                        onChange={(event) =>
                          setQuestionForm((previous) => ({
                            ...previous,
                            choices: previous.choices.map((item, idx) =>
                              idx === index ? event.target.value : item
                            ),
                          }))
                        }
                      />
                      <Button
                        color="error"
                        disabled={questionForm.choices.length <= 2}
                        onClick={() =>
                          setQuestionForm((previous) => {
                            const nextChoices = previous.choices.filter((_, idx) => idx !== index);
                            const nextCorrect =
                              previous.correct_choice_index >= nextChoices.length
                                ? Math.max(nextChoices.length - 1, 0)
                                : previous.correct_choice_index;
                            return {
                              ...previous,
                              choices: nextChoices,
                              correct_choice_index: nextCorrect,
                            };
                          })
                        }
                      >
                        Suppr.
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() =>
                      setQuestionForm((previous) => ({
                        ...previous,
                        choices: [...previous.choices, ""],
                      }))
                    }
                  >
                    + Ajouter un choix
                  </Button>
                </div>
              )}

              {questionForm.type === "short_answer" || questionForm.type === "number" ? (
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">
                    {questionForm.type === "number"
                      ? "Réponse correcte (nombre)"
                      : "Réponse correcte"}
                  </label>
                  <InputField
                    type={questionForm.type === "number" ? "number" : "text"}
                    value={questionForm.correct_value}
                    onChange={(event) =>
                      setQuestionForm((previous) => ({
                        ...previous,
                        correct_value: event.target.value,
                      }))
                    }
                  />
                </div>
              ) : null}
            </div>

            {questionError ? <p className="text-xs text-red-500">{questionError}</p> : null}

            <div className="flex justify-end gap-2">
              <Button onClick={() => setQuestionForm(defaultDraftQuestion)}>Réinitialiser</Button>
              <ButtonLoading
                loading={false}
                label={questionForm.localId ? "Mettre à jour la question" : "Ajouter la question"}
                onClick={addOrUpdateQuestionDraft}
              />
            </div>

            <div className="space-y-2">
              {draftQuestions.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune question ajoutée pour le moment.</p>
              ) : null}
              {draftQuestions.map((question, index) => (
                <div
                  key={question.localId}
                  className="rounded border border-gray-200 p-2 flex items-start justify-between gap-2"
                >
                  <div>
                    <p className="text-xs text-gray-500">
                      #{index + 1} - {question.type} - {question.points} point(s)
                    </p>
                    <p className="font-medium text-sm">{question.question_text || "(Sans texte)"}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setQuestionForm(question)}>Modifier</Button>
                    <Button color="error" onClick={() => removeQuestionDraft(question.localId)}>
                      Supprimer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="border rounded-lg p-3 space-y-3">
            <h4 className="font-medium">Validation avant création</h4>
            <div className="text-sm bg-gray-50 rounded p-3 space-y-1">
              <p>
                <span className="font-medium">Titre:</span> {title || "-"}
              </p>
              <p>
                <span className="font-medium">Description:</span> {description || "-"}
              </p>
              <p>
                <span className="font-medium">Système:</span>{" "}
                {systems.find((item) => item.id === Number(educationalSystemId))?.title || "-"}
              </p>
              <p>
                <span className="font-medium">Niveau:</span>{" "}
                {levels.find((item) => item.id === Number(levelId))?.title || "-"}
              </p>
              <p>
                <span className="font-medium">Portée:</span> {isGlobal ? "Global" : "Spécifique"}
              </p>
              <p>
                <span className="font-medium">Questions:</span> {draftQuestions.length}
              </p>
            </div>
            <div className="space-y-2">
              {draftQuestions.map((question, index) => (
                <div key={question.localId} className="rounded border border-gray-200 p-2 text-sm">
                  <p className="text-xs text-gray-500">
                    #{index + 1} - {question.type} - {question.points} point(s)
                  </p>
                  <p className="font-medium">{question.question_text || "(Sans texte)"}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {error ? <p className="text-xs text-red-500">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={handleClose}>Annuler</Button>
          {step > 1 ? <Button onClick={goToPreviousStep}>Retour</Button> : null}
          {step < 3 ? (
            <Button color="primary" onClick={goToNextStep}>
              Continuer
            </Button>
          ) : (
            <ButtonLoading loading={loading} label="Créer et associer" onClick={handleSubmit} />
          )}
        </div>
      </div>
    </Modal>
  );
}
